import {
    NATIVE_INVOICE_PAGE_SIZE_CSS,
    type NativeInvoicePageSize,
} from "../constants/nativeInvoice";

const CSS_PX_PER_INCH = 96;
const CSS_PX_PER_MM = CSS_PX_PER_INCH / 25.4;

export const NATIVE_INVOICE_PAGE_SIZE_PX: Record<
    NativeInvoicePageSize,
    { width: number; height: number }
> = {
    letter: { width: 8.5 * CSS_PX_PER_INCH, height: 11 * CSS_PX_PER_INCH },
    a4: { width: 210 * CSS_PX_PER_MM, height: 297 * CSS_PX_PER_MM },
};

const PADDING_TOP_PX = 48;
const PADDING_BOTTOM_PX = 20;
const MASTHEAD_PX = 96;
const INFO_GRID_MARGIN_BOTTOM_PX = 36;
const INFO_BLOCK_PX = 46;
const INFO_BLOCK_GAP_PX = 16;
const PARTY_LABEL_PX = 22;
const PARTY_LINE_PX = 18;
// The info-grid meta column (1fr of a 3-column grid) is ~200px wide; at the
// meta label/value font sizes that fits roughly this many characters per line.
// Kept deliberately conservative (small) so a borderline field is budgeted as
// wrapping rather than overflowing the printed page.
const META_CHARS_PER_LINE = 24;
const META_LINE_WRAP_PX = 20;
const TABLE_HEADER_PX = 35;
const LINE_ROW_PX = 45;
const LINE_ROW_WITH_DESCRIPTION_PX = 67;
const LINE_WRAP_PX = 20;
const CHARS_PER_LINE = 36;
const TOTAL_ROW_PX = 26;
const TOTAL_GRAND_PX = 46;
const BOTTOM_SECTION_CHROME_PX = 75;
const BOTTOM_TEXT_LINE_PX = 20;
const PAY_BUTTON_PX = 54;
const FOOTER_PX = 35;
const SAFETY_SLACK_PX = 16;

export interface NativeInvoicePaginationLine {
    productOrService?: string | null;
    description?: string | null;
}

export interface NativeInvoiceMetaField {
    label: string;
    value: string;
}

export interface PaginateNativeInvoiceDocumentInput<
    TLine extends NativeInvoicePaginationLine,
> {
    pageSize: NativeInvoicePageSize;
    lines: TLine[];
    footer: string;
    // The "1 of 2" indicator renders in the footer strip, so it reserves the
    // same footer height even when the footer text is empty.
    showPagination: boolean;
    // The shown info-grid meta fields (invoice number, PO number, Terms, invoice
    // date, due date), carrying each field's label + value so height estimation
    // can account for text that wraps in the narrow meta column.
    metaFields: NativeInvoiceMetaField[];
    fromLineCount: number;
    toLineCount: number;
    showSubtotal: boolean;
    showTax: boolean;
    showDiscount: boolean;
    showDeposit: boolean;
    showTotalDue: boolean;
    terms: string;
    note: string;
    hasPayButton: boolean;
}

export interface NativeInvoiceDocumentPage<
    TLine extends NativeInvoicePaginationLine,
> {
    lines: TLine[];
    showMasthead: boolean;
    showTable: boolean;
    showClosing: boolean;
    pageNumber: number;
    pageCount: number;
    footer: string;
}

interface NativeInvoicePartyLineCountInput {
    name: string;
    fullName: string;
    company?: string;
    showEmail: boolean;
    addressLines: string[];
    phoneNumber: string;
    taxId: string;
    sender?: string;
}

export function paginateNativeInvoiceDocument<
    TLine extends NativeInvoicePaginationLine,
>(
    input: PaginateNativeInvoiceDocumentInput<TLine>,
): Array<NativeInvoiceDocumentPage<TLine>> {
    const pageContentHeight =
        NATIVE_INVOICE_PAGE_SIZE_PX[input.pageSize].height -
        PADDING_TOP_PX -
        PADDING_BOTTOM_PX;
    const footerHeight = input.footer || input.showPagination ? FOOTER_PX : 0;
    const closingHeight = estimateClosingHeight(input);
    const firstPageChrome =
        MASTHEAD_PX +
        estimateInfoGridHeight(input) +
        TABLE_HEADER_PX +
        footerHeight +
        SAFETY_SLACK_PX;
    const continuationChrome = TABLE_HEADER_PX + footerHeight + SAFETY_SLACK_PX;

    const remaining = [...input.lines];
    const packed: Array<
        Pick<
            NativeInvoiceDocumentPage<TLine>,
            "lines" | "showMasthead" | "showTable" | "showClosing"
        >
    > = [];

    if (remaining.length === 0) {
        packed.push({
            lines: [],
            showMasthead: true,
            showTable: true,
            showClosing: true,
        });
    }

    while (remaining.length > 0) {
        const isFirstPage = packed.length === 0;
        const chrome = isFirstPage ? firstPageChrome : continuationChrome;
        let available = pageContentHeight - chrome;
        const pageLines: TLine[] = [];

        while (remaining.length > 0) {
            const lineHeight = estimateLineHeight(remaining[0]);
            if (pageLines.length > 0 && lineHeight > available) {
                break;
            }
            const line = remaining.shift();
            if (!line) {
                break;
            }
            pageLines.push(line);
            available -= lineHeight;
        }

        packed.push({
            lines: pageLines,
            showMasthead: isFirstPage,
            showTable: true,
            showClosing: false,
        });
    }

    const lastPage = packed[packed.length - 1];
    if (lastPage) {
        const lastChrome = lastPage.showMasthead
            ? firstPageChrome
            : continuationChrome;
        const usedByLines = lastPage.lines.reduce(
            (sum, line) => sum + estimateLineHeight(line),
            0,
        );
        const leftover = pageContentHeight - lastChrome - usedByLines;
        if (leftover >= closingHeight) {
            lastPage.showClosing = true;
        } else {
            packed.push({
                lines: [],
                showMasthead: false,
                showTable: false,
                showClosing: true,
            });
        }
    }

    const pageCount = packed.length;
    return packed.map((page, index) => ({
        ...page,
        pageNumber: index + 1,
        pageCount,
        footer: input.footer,
    }));
}

export type NativeInvoicePageBoxCss =
    (typeof NATIVE_INVOICE_PAGE_SIZE_CSS)[NativeInvoicePageSize];

export function nativeInvoicePageBoxCss(
    pageSize: NativeInvoicePageSize,
): NativeInvoicePageBoxCss {
    return NATIVE_INVOICE_PAGE_SIZE_CSS[pageSize];
}

export function countNativeInvoicePartyLines({
    name,
    fullName,
    company,
    showEmail,
    addressLines,
    phoneNumber,
    taxId,
    sender,
}: NativeInvoicePartyLineCountInput): number {
    let count = 0;
    if (name) {
        count += 1;
    }
    if (fullName) {
        count += 1;
    }
    if (company) {
        count += 1;
    }
    if (sender) {
        count += 1;
    }
    if (showEmail) {
        count += 1;
    }
    count += addressLines.length;
    if (phoneNumber) {
        count += 1;
    }
    if (taxId) {
        count += 1;
    }
    return Math.max(count, 1);
}

function estimateMetaFieldHeight({
    label,
    value,
}: NativeInvoiceMetaField): number {
    // Both the label and the value can wrap independently in the meta column;
    // each extra wrapped line adds a line's worth of height on top of the
    // single-line INFO_BLOCK_PX budget.
    const extraLines =
        extraWrappedLines(label, META_CHARS_PER_LINE) +
        extraWrappedLines(value, META_CHARS_PER_LINE);
    return INFO_BLOCK_PX + extraLines * META_LINE_WRAP_PX;
}

function estimateInfoGridHeight({
    metaFields,
    fromLineCount,
    toLineCount,
}: Pick<
    PaginateNativeInvoiceDocumentInput<NativeInvoicePaginationLine>,
    "metaFields" | "fromLineCount" | "toLineCount"
>): number {
    const metaHeight =
        metaFields.length === 0
            ? 0
            : metaFields.reduce(
                  (sum, field) => sum + estimateMetaFieldHeight(field),
                  0,
              ) +
              Math.max(0, metaFields.length - 1) * INFO_BLOCK_GAP_PX;
    const fromHeight = PARTY_LABEL_PX + fromLineCount * PARTY_LINE_PX;
    const toHeight = PARTY_LABEL_PX + toLineCount * PARTY_LINE_PX;
    return (
        Math.max(metaHeight, fromHeight, toHeight) + INFO_GRID_MARGIN_BOTTOM_PX
    );
}

function estimateLineHeight(line: NativeInvoicePaginationLine): number {
    const name = line.productOrService ?? line.description ?? "";
    const hasDescription = Boolean(line.productOrService && line.description);
    let height = hasDescription ? LINE_ROW_WITH_DESCRIPTION_PX : LINE_ROW_PX;
    height += extraWrappedLines(name) * LINE_WRAP_PX;
    if (hasDescription && line.description) {
        height += extraWrappedLines(line.description) * LINE_WRAP_PX;
    }
    return height;
}

function extraWrappedLines(
    text: string,
    charsPerLine: number = CHARS_PER_LINE,
): number {
    if (!text) {
        return 0;
    }
    return Math.max(0, Math.ceil(text.length / charsPerLine) - 1);
}

function estimateClosingHeight({
    showSubtotal,
    showTax,
    showDiscount,
    showDeposit,
    showTotalDue,
    terms,
    note,
    hasPayButton,
}: Pick<
    PaginateNativeInvoiceDocumentInput<NativeInvoicePaginationLine>,
    | "showSubtotal"
    | "showTax"
    | "showDiscount"
    | "showDeposit"
    | "showTotalDue"
    | "terms"
    | "note"
    | "hasPayButton"
>): number {
    // TOTAL_GRAND_PX is the Total due row's own height (it is visually
    // heavier than a plain TOTAL_ROW_PX row); when it's hidden the closing
    // block shrinks to the plain row height instead.
    let height = showTotalDue ? TOTAL_GRAND_PX : TOTAL_ROW_PX;
    if (showSubtotal) {
        height += TOTAL_ROW_PX;
    }
    if (showTax) {
        height += TOTAL_ROW_PX;
    }
    if (showDiscount) {
        height += TOTAL_ROW_PX;
    }
    if (showDeposit) {
        height += TOTAL_ROW_PX;
    }
    if (terms || note) {
        const text = [terms, note].filter(Boolean).join("\n");
        const textLines = text
            .split("\n")
            .reduce(
                (count, segment) =>
                    count +
                    Math.max(1, Math.ceil(segment.length / CHARS_PER_LINE)),
                0,
            );
        height += BOTTOM_SECTION_CHROME_PX + textLines * BOTTOM_TEXT_LINE_PX;
    }
    if (hasPayButton) {
        height += PAY_BUTTON_PX;
    }
    return height;
}
